// Right-hand sidebar for the patient portal's visit view.
//
// A port of `pages/medstudent/MedStudentRightPanel.js` down to two tabs —
// Reports and AI Chat. Live Transcript, Copilot and the parked Process Review
// tab are clinician surfaces and are deliberately absent.
//
// ── Tailwind scoping ───────────────────────────────────────────────────────
// tailwind.config.js sets `important: '#clinic-root'`, so every utility is
// emitted as `#clinic-root .util`. This panel therefore renders its own
// `<div id="clinic-root">` wrapper: without it every class here is inert and
// the sidebar collapses into an unstyled column. The wrapper is placed on the
// OUTERMOST element (a utility never applies to the element carrying the id,
// only to its descendants) and wraps only this panel, so the `!important`
// Tailwind rules can't leak onto the Chakra summary panel beside it.
// `./src/components/patient/**` also had to be added to tailwind's `content`
// globs or the classes unique to this file are never generated at all.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { groupReportsByClassification } from "../../pages/medstudent/process/reportCategories";
import { isPatientRole } from "../../config/roles";
import PatientAIAssistant from "./PatientAIAssistant";
import PatientMeds from "../../pages/patient/PatientMeds";
import PatientReportUpload from "./PatientReportUpload";
import { fetchPatientReports, fileExtOf, formatBytes } from "./patientPortalApi";

const TABS = [
  {
    key: "ai",
    label: "AI Chat",
    icon: "✨",
    activeCls:
      "border-blue-500 text-blue-600 dark:text-blue-300 bg-blue-50/60 dark:bg-blue-500/[0.10]",
    idleCls:
      "text-blue-500/80 dark:text-blue-400/80 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50/40 dark:hover:bg-blue-500/[0.06]",
  },
  {
    key: "reports",
    label: "Reports",
    icon: "📁",
    activeCls:
      "border-amber-500 text-amber-600 dark:text-amber-300 bg-amber-50/60 dark:bg-amber-500/[0.10]",
    idleCls:
      "text-amber-500/80 dark:text-amber-400/80 hover:text-amber-600 dark:hover:text-amber-300 hover:bg-amber-50/40 dark:hover:bg-amber-500/[0.06]",
  },
  {
    key: "meds",
    label: "Meds",
    icon: "💊",
    patientOnly: true,
    activeCls:
      "border-emerald-500 text-emerald-600 dark:text-emerald-300 bg-emerald-50/60 dark:bg-emerald-500/[0.10]",
    idleCls:
      "text-emerald-500/80 dark:text-emerald-400/80 hover:text-emerald-600 dark:hover:text-emerald-300 hover:bg-emerald-50/40 dark:hover:bg-emerald-500/[0.06]",
  },
];

const MIN_WIDTH = 300;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 420;

// ── Reports tab ─────────────────────────────────────────────────────────────
// Files come from `/api/patient/reports/` already signed and classified, so
// this tab never touches Supabase storage directly — the patient's anon key has
// no business listing chart folders.
function ReportsTab({ appointmentId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [previewing, setPreviewing] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const list = await fetchPatientReports(appointmentId);
        if (!cancelled) setFiles(list);
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appointmentId, refreshKey]);

  // Opening a different file always starts inline, never in the expanded
  // overlay the previous file may have been left in.
  useEffect(() => {
    setIsExpanded(false);
  }, [previewing]);

  // Esc leaves the overlay. Only bound while expanded so the rest of the page
  // keeps its own Esc handling.
  useEffect(() => {
    if (!isExpanded) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setIsExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isExpanded]);

  const grouped = useMemo(() => groupReportsByClassification(files), [files]);

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
      {!previewing && (
        <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800/60">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {files.length} file{files.length === 1 ? "" : "s"}
            </div>
            <PatientReportUpload
              onUploaded={() => setRefreshKey((k) => k + 1)}
              renderTrigger={({ onClick, disabled, busy }) => (
                <button
                  type="button"
                  onClick={onClick}
                  disabled={disabled}
                  title="Upload a report for your care team"
                  className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold shadow-sm ring-1 ring-amber-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-amber-500 hover:bg-amber-400 hover:shadow-md text-white"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  {busy ? "Uploading…" : "Upload"}
                </button>
              )}
            />
          </div>
        </div>
      )}

      {!previewing && loading && (
        <div className="p-4 text-sm text-slate-500 dark:text-slate-400">Loading your reports…</div>
      )}
      {!previewing && error && (
        <div className="p-4 text-sm text-red-600 dark:text-red-400">{error}</div>
      )}
      {!previewing && !loading && !error && files.length === 0 && (
        <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
          No reports yet. Anything you upload here goes to your care team and shows
          up on your chart.
        </div>
      )}

      {!previewing && files.length > 0 && (
        <div className="flex-1 overflow-y-auto scrollbar-thin px-3 py-3 space-y-3">
          {grouped.map((g) => (
            <div key={g.classification}>
              <div className="px-1 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 flex items-center gap-2">
                <span>{g.label}</span>
                <span className="text-slate-400 dark:text-slate-500 font-normal normal-case tracking-normal">
                  ({g.items.length})
                </span>
              </div>
              <div className="space-y-1.5">
                {g.items.map((f) => {
                  const ext = fileExtOf(f.name);
                  return (
                    <button
                      key={f.path || f.name}
                      type="button"
                      onClick={() => setPreviewing({ ...f, ext })}
                      className="w-full text-left p-3 rounded-lg border border-slate-200 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40 flex items-center gap-3"
                    >
                      <div className="w-9 h-9 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold uppercase shrink-0">
                        {ext || "·"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                          {f.name}
                        </div>
                        {f.uploaded_by_patient && (
                          <span className="mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30">
                            You uploaded this
                          </span>
                        )}
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                          {formatBytes(f.size)}
                          {f.created_at ? ` · ${new Date(f.created_at).toLocaleDateString()}` : ""}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {previewing && (
        <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800/60">
            <div
              className="flex-1 min-w-0 text-sm font-semibold text-slate-900 dark:text-slate-100 truncate"
              title={previewing.name}
            >
              {previewing.name}
            </div>
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              title={isExpanded ? "Collapse to sidebar" : "Expand preview"}
              aria-label={isExpanded ? "Collapse preview" : "Expand preview"}
              className="shrink-0 text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-200"
            >
              {isExpanded ? "⤡" : "⤢"}
            </button>
            <button
              type="button"
              onClick={() => setPreviewing(null)}
              className="shrink-0 text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-200"
            >
              ← Back
            </button>
          </div>
          {!isExpanded && (
            <div className="flex-1 bg-slate-100 dark:bg-slate-900/40" style={{ minHeight: 0 }}>
              {renderPreviewBody(previewing)}
            </div>
          )}
        </div>
      )}

      {previewing && isExpanded && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex flex-col">
          <div className="m-6 flex-1 rounded-2xl border border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] shadow-2xl overflow-hidden flex flex-col">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 dark:border-slate-800/60">
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate flex-1">
                {previewing.name}
              </div>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                aria-label="Collapse preview"
                className="text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-200"
              >
                ⤡
              </button>
              <button
                type="button"
                onClick={() => setPreviewing(null)}
                aria-label="Close preview"
                className="text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-200"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 bg-slate-100 dark:bg-slate-900/40" style={{ minHeight: 0 }}>
              {renderPreviewBody(previewing)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Shared by the inline pane and the expanded overlay so both render a file
// exactly the same way.
function renderPreviewBody(previewing) {
  if (!previewing) return null;
  const { ext, url, name } = previewing;
  if (!url) {
    return (
      <div className="p-4 text-sm text-slate-600 dark:text-slate-300">
        This file's link expired. Reopen the panel to refresh it.
      </div>
    );
  }
  if (ext === "pdf") {
    // #view=FitH scales the page to the panel width; #toolbar=0 reclaims the
    // vertical space the viewer chrome would eat in a narrow sidebar.
    return (
      <iframe title={name} src={`${url}#view=FitH&toolbar=0`} className="w-full h-full border-0" />
    );
  }
  if (["csv", "txt"].includes(ext)) {
    return <iframe title={name} src={url} className="w-full h-full border-0" />;
  }
  if (["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"].includes(ext)) {
    return (
      <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
        <img src={url} alt={name} className="max-w-full max-h-full object-contain" />
      </div>
    );
  }
  return (
    <div className="p-4 text-sm text-slate-600 dark:text-slate-300">
      Preview isn't available for .{ext}.{" "}
      <a className="underline text-blue-600 dark:text-blue-300" href={url} target="_blank" rel="noreferrer">
        Open in a new tab
      </a>
    </div>
  );
}

export default function PatientRightPanel({
  appointmentId,
  patientFirstName,
  patientName,
  userInitials,
  defaultTab = "ai",
  role,
  // The panel normally supplies its own #clinic-root so its Tailwind classes
  // resolve (see the note at the top of this file). Pass false when an ancestor
  // ALREADY carries that id — MedStudentClinicPage does — because a duplicate
  // id is invalid HTML and would shadow the outer node for the
  // document.getElementById('clinic-root') portal targets used by the clinic
  // modals.
  provideClinicRoot = true,
}) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [collapsed, setCollapsed] = useState(false);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const dragRef = useRef(null);

  const showPatientOnlyTabs = isPatientRole(role);
  const visibleTabs = useMemo(
    () => TABS.filter((t) => !t.patientOnly || showPatientOnlyTabs),
    [showPatientOnlyTabs]
  );

  // Drag-to-resize. The panel is on the RIGHT edge, so dragging left (a
  // negative delta) has to make it wider — hence `startWidth - dx`.
  const onDragStart = useCallback(
    (e) => {
      dragRef.current = { startX: e.clientX, startWidth: width };
      e.preventDefault();
    },
    [width]
  );

  useEffect(() => {
    const onMove = (e) => {
      const op = dragRef.current;
      if (!op) return;
      const next = op.startWidth - (e.clientX - op.startX);
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, next)));
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  if (collapsed) {
    return (
      // Inline styles, not Tailwind: `important: '#clinic-root'` emits
      // `#clinic-root .util`, which matches DESCENDANTS only — a utility on the
      // element carrying the id is silently inert.
      <div
        id={provideClinicRoot ? "clinic-root" : undefined}
        style={{ flexShrink: 0, display: "flex" }}
      >
        <div className="h-full w-11 border-l border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] flex flex-col items-center py-3 gap-3">
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            aria-label="Expand panel"
            title="Expand panel"
            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setActiveTab(tab.key);
                setCollapsed(false);
              }}
              aria-label={tab.label}
              title={tab.label}
              className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800/60"
            >
              <span aria-hidden>{tab.icon}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    // Inline styles for the same reason as the collapsed rail above: Tailwind
    // utilities never apply to the #clinic-root element itself, only inside it.
    <div
      id={provideClinicRoot ? "clinic-root" : undefined}
      style={{ flexShrink: 0, display: "flex", minHeight: 0 }}
    >
      {/* Resize grip */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panel"
        onMouseDown={onDragStart}
        className="w-1.5 cursor-col-resize bg-transparent hover:bg-blue-400/40 transition-colors"
      />
      <aside
        style={{ width: `${width}px` }}
        className="shrink-0 border-l border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] flex flex-col"
      >
        <div className="border-b border-slate-200 dark:border-slate-800/60 flex flex-wrap items-stretch">
          {visibleTabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                aria-pressed={isActive}
                className={[
                  "flex-1 px-2 py-2.5 text-[11px] font-semibold flex items-center justify-center gap-1.5 border-b-2 transition-colors",
                  isActive ? tab.activeCls : `border-transparent ${tab.idleCls}`,
                ].join(" ")}
              >
                <span aria-hidden>{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            aria-label="Collapse panel"
            title="Collapse panel"
            className="px-3 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 border-l border-slate-200 dark:border-slate-800/60"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Both tabs stay MOUNTED (the inactive one hidden) so switching to
            Reports and back doesn't wipe the chat — history lives in component
            state here, unlike the clinic assistant's server-backed threads. */}
        <div className={activeTab === "ai" ? "flex-1 flex flex-col" : "hidden"} style={{ minHeight: 0 }}>
          <PatientAIAssistant
            appointmentId={appointmentId}
            patientFirstName={patientFirstName}
            patientName={patientName}
            userInitials={userInitials}
          />
        </div>
        <div className={activeTab === "reports" ? "flex-1 flex flex-col" : "hidden"} style={{ minHeight: 0 }}>
          <ReportsTab appointmentId={appointmentId} />
        </div>
        {showPatientOnlyTabs && (
          <div className={activeTab === "meds" ? "flex-1 overflow-y-auto scrollbar-thin" : "hidden"} style={{ minHeight: 0 }}>
            <PatientMeds embedded />
          </div>
        )}
      </aside>
    </div>
  );
}
